import CommonAvatar from "@/components/Common/Avatar";
import DataTable from "@/components/Common/DataTable";
import DataTableColumnHeader from "@/components/Common/DataTable/DataTableColumnHeader";
import CommonImg from "@/components/Common/Img";
import { SCENARIO_DECISION_LABELS, ScenarioDecision } from "@/consts/Scenario";
import { cn, formatDate } from "@/libs/utils";
import { ScenarioStages } from "@/types/scenario-approvals";
import { User } from "@/types/user";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import CalendarSvg from "@/assets/images/calendar.svg";

type Props = {
  stages: ScenarioStages[];
};

type ApprovalUser = {
  user: User;
  decision: ScenarioDecision;
  updated_at?: number;
  reason?: string;
};

type TableDataProps = {
  stage: string;
  approvers: ApprovalUser[];
  referees: User[];
};

export default function ApprovalDetailReasons({ stages }: Props) {
  const data = useMemo(() => {
    const rows: TableDataProps[] = [];
    stages.forEach((stage, index) => {
      rows.push({
        stage: String(index + 1),
        approvers: stage.to_list,
        referees: stage.cc_list.map((item) => item.user),
      });
    });

    return rows;
  }, [stages]);

  const columns = useMemo<ColumnDef<TableDataProps>[]>(
    () => [
      {
        accessorKey: "stage",
        header: ({ column }) => (
          <DataTableColumnHeader
            title="승인 단계"
            column={column}
            className="text-center"
          />
        ),
        cell: ({ row }) => (
          <p className="text-gray-A text-xs font-normal text-center">
            {row.original.stage}
          </p>
        ),
        enableSorting: false,
        size: 90,
      },
      {
        accessorKey: "approvers",
        header: ({ column }) => (
          <DataTableColumnHeader title="승인자" column={column} />
        ),
        cell: ({ row }) => {
          const approvers = row.original.approvers;
          return (
            <div className="flex flex-col">
              {approvers.map((approver, index) => (
                <div
                  key={`${approver.user.user_id}-${index}`}
                  className={cn(
                    "flex items-center h-12 py-2",
                    index < approvers.length - 1 && "border-b border-gray-200"
                  )}
                >
                  <CommonAvatar
                    img_url={approver.user.img_url}
                    alt={approver.user.user_name}
                    name={approver.user.user_name}
                  />
                </div>
              ))}
            </div>
          );
        },
        enableSorting: false,
        size: 120,
      },
      {
        accessorKey: "decision",
        header: ({ column }) => (
          <DataTableColumnHeader
            title="진행상태"
            column={column}
            className="text-center"
          />
        ),
        cell: ({ row }) => {
          const approvers = row.original.approvers;

          return (
            <div className="flex flex-col">
              {approvers.map((approver, index) => (
                <div
                  key={`${approver.user.user_id}-${index}`}
                  className={cn(
                    "flex items-center justify-center h-12 py-2",
                    index < approvers.length - 1 && "border-b border-gray-200"
                  )}
                >
                  <p
                    className={cn(
                      "text-xs font-semibold text-center",
                      approver.decision === ScenarioDecision.APPROVED
                        ? "text-green"
                        : approver.decision === ScenarioDecision.REJECTED
                          ? "text-pink"
                          : "text-gray-B"
                    )}
                  >
                    {SCENARIO_DECISION_LABELS[approver.decision]}
                  </p>
                </div>
              ))}
            </div>
          );
        },
        enableSorting: false,
        size: 85,
      },
      {
        accessorKey: "updated_at",
        header: ({ column }) => (
          <DataTableColumnHeader title="응답시점" column={column} />
        ),
        cell: ({ row }) => {
          const approvers = row.original.approvers;

          return (
            <div className="flex flex-col">
              {approvers.map((approver, index) => (
                <div
                  key={`${approver.user.user_id}-${index}`}
                  className={cn(
                    "flex items-center gap-1 h-12 py-2",
                    index < approvers.length - 1 && "border-b border-gray-200"
                  )}
                >
                  <CommonImg
                    src={CalendarSvg}
                    alt="calendar"
                    className="text-gray-A"
                  />
                  <span className="text-xs text-gray-A font-normal">
                    {approver.updated_at
                      ? formatDate(approver.updated_at)
                      : "-"}
                  </span>
                </div>
              ))}
            </div>
          );
        },
        enableSorting: false,
        size: 150,
      },
      {
        accessorKey: "reason",
        header: ({ column }) => (
          <DataTableColumnHeader title="승인/반려 사유" column={column} />
        ),
        cell: ({ row }) => {
          const approvers = row.original.approvers;

          return (
            <div className="flex flex-col">
              {approvers.map((approver, index) => (
                <div
                  key={`${approver.user.user_id}-${index}`}
                  className={cn(
                    "flex items-center h-12 py-2",
                    index < approvers.length - 1 && "border-b border-gray-200"
                  )}
                >
                  <p className="text-sm text-gray-A font-normal">
                    {approver.reason || ""}
                  </p>
                </div>
              ))}
            </div>
          );
        },
        enableSorting: false,
        size: 400,
      },
      {
        accessorKey: "referees",
        header: ({ column }) => (
          <DataTableColumnHeader title="참조자" column={column} />
        ),
        cell: ({ row }) => {
          const referees = row.original.referees;

          if (referees.length === 0) {
            return <span className="text-gray-A text-xs">-</span>;
          }

          return (
            <div className="flex flex-wrap gap-1 max-w-[200px]">
              {referees.map((referee, index) => (
                <CommonAvatar
                  key={`${referee.user_id}-${index}`}
                  img_url={referee.img_url}
                  alt={referee.user_name}
                  name={referee.user_name}
                />
              ))}
            </div>
          );
        },
        enableSorting: false,
        size: 250,
      },
    ],
    []
  );

  return (
    <div className="p-5 max-h-96 overflow-y-auto">
      <DataTable
        data={data}
        columns={columns}
        isPagination={false}
        tableCellClassName="py-2 px-3"
      />
    </div>
  );
}
